import { useLazyQuery } from '@apollo/client'
import { useMemo } from 'react'

import { navSearchAutocompleteQuery } from '../../app/_components/chrome/PrimaryNavSearch.client'
import { createPdpUrl } from '../../app/_components/navigation/createPdpUrl'
import {
  derivePricingForProductGroup,
  derivePricingForVariantGroup,
} from '../../app/_components/pricing/PricingRange.client'
import { useLocalization } from '../../app/_providers/LocalizationProvider.client'
import { isGiftCardHandle } from '../../components/pages/products/ProductPage/hooks/useIsGiftCard'
import { SearchAutocompleteProduct } from '../../types/graphql/search'

export function useSearchAutocomplete() {
  const [fetchSearchAutocompleteResults, { data, previousData, error, loading }] = useLazyQuery(
    navSearchAutocompleteQuery,
    { notifyOnNetworkStatusChange: true }
  )
  const { region } = useLocalization()

  const products: SearchAutocompleteProduct[] = useMemo(() => {
    if (data?.productCatalogSearch?.nodes) {
      return data.productCatalogSearch.nodes.map(node => {
        const variantGroup = node.variantGroup
        return {
          id: variantGroup.id,
          categoryAnalyticsId: variantGroup.primaryCategory.analyticsId,
          deprecatedProductHandle: `${variantGroup.product.handle}${
            variantGroup.color?.id ? '-' : ''
          }${variantGroup.color?.id}`,
          colorKey: variantGroup.color?.id,
          productGroupHandle: variantGroup.product.handle,
          shopifyId: variantGroup.defaultVariant.shopifyProductId,
          title: variantGroup.seoTitle,
          url: variantGroup.urlPath,
          imageSrc: variantGroup.images?.[0]?.url,
          pricing: derivePricingForVariantGroup(variantGroup),
          price: variantGroup.defaultVariant.price.amount,
          discountPrice: variantGroup.defaultVariant.compareAtPrice?.amount
            ? variantGroup.defaultVariant.price.amount
            : undefined,
          currencyCode: variantGroup.defaultVariant.price.currencyCode,
          isGiftCard: variantGroup.primaryCategory.key === 'GIFT_CARD',
        }
      })
    } else {
      return (data?.search.items.nodes ?? []).map(node => {
        const product = node.product
        return {
          id: product.id,
          categoryAnalyticsId: product.rawCategory,
          deprecatedProductHandle: product.handle ?? product.productGroup.handle,
          colorKey: product.colorInfo?.rawName,
          productGroupHandle: product.productGroup.handle,
          shopifyId: product.shopifyId,
          title: product.seoTitle?.trim().length ? product.seoTitle : product.title,
          url: createPdpUrl({
            productGroupHandle: product.productGroup.handle,
            isExclusive: product.exclusive,
            isKit: product.rawCategory === 'Product Kit',
            genderKey: product.genderCategory,
            collectionHandle: null,
            colorKey: product.colorInfo?.rawName,
            fitKey: product.rawFit,
            sizeKey: product.defaultVariant.rawSize,
          }),
          imageSrc: product.images?.nodes[0]?.source,
          pricing: product.defaultVariant.priceDetails?.price
            ? derivePricingForProductGroup(
                {
                  products: [
                    {
                      priceRange: product.productGroup.priceRange,
                      defaultVariant: {
                        price: product.defaultVariant.priceDetails.price.amount,
                        discountPrice: product.defaultVariant.priceDetails.discountPrice?.amount,
                      },
                    },
                  ],
                },
                region.currency
              )
            : undefined,
          price: (product.defaultVariant.priceDetails!.discountPrice?.amount ??
            product.defaultVariant.priceDetails!.price!.amount) as number,
          discountPrice: product.defaultVariant.priceDetails!.discountPrice?.amount as
            | number
            | undefined,
          currencyCode: product.defaultVariant.priceDetails!.price!.currency,
          isGiftCard: isGiftCardHandle(product.productGroup.handle),
        }
      })
    }
  }, [data?.productCatalogSearch?.nodes, data?.search.items.nodes, region.currency])

  const suggestions: string[] = useMemo(() => {
    const v2Suggestions =
      data?.productCatalogSearchSuggester?.nodes.map(suggestionNode => suggestionNode.query!) ??
      previousData?.productCatalogSearchSuggester?.nodes.map(
        suggestionNode => suggestionNode.query!
      ) ??
      []

    const v1Suggestions =
      data?.search?.suggestions?.map(suggestion => suggestion.query) ??
      previousData?.search?.suggestions?.map(suggestion => suggestion.query) ??
      []

    if (v2Suggestions.length > 0) {
      return v2Suggestions
    } else {
      return v1Suggestions
    }
  }, [data, previousData])

  const queryRan: boolean = !!data || !!error
  const noResult: boolean = !loading && products.length === 0 && queryRan

  return { fetchSearchAutocompleteResults, products, suggestions, error, noResult }
}
